export const resumeSessionStorageKey = 'aareal-aval-resume';

export type ResumeSessionState = {
  cid: string;
  href: string;
};

function isBaseApplicationHref(state: ResumeSessionState) {
  return state.href === `/${state.cid}`;
}

function isStepApplicationHref(state: ResumeSessionState) {
  return state.href.startsWith(`/${state.cid}/`);
}

export function readResumeSession(): ResumeSessionState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(resumeSessionStorageKey);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;

    if (
      typeof parsedValue === 'object' &&
      parsedValue !== null &&
      'cid' in parsedValue &&
      'href' in parsedValue &&
      typeof parsedValue.cid === 'string' &&
      typeof parsedValue.href === 'string'
    ) {
      return {
        cid: parsedValue.cid,
        href: parsedValue.href,
      };
    }
  } catch {
    return null;
  }

  return null;
}

export function writeResumeSession(state: ResumeSessionState) {
  if (typeof window === 'undefined') {
    return;
  }

  const existingState = readResumeSession();
  const shouldKeepExistingStep =
    existingState?.cid === state.cid &&
    isBaseApplicationHref(state) &&
    isStepApplicationHref(existingState);

  const nextState = shouldKeepExistingStep ? existingState : state;

  window.sessionStorage.setItem(resumeSessionStorageKey, JSON.stringify(nextState));

  try {
    window.dispatchEvent(new CustomEvent<ResumeSessionState>('resume-session-changed', { detail: nextState }));
  } catch {
    // Swallow dispatch errors – storage update is the important side effect.
  }
}

export function clearResumeSession() {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(resumeSessionStorageKey);
}
